// E-Learning, the video help library (PA/KPA, PPK, Penyedia): short videos to
// open when something about procurement is confusing. No progress, no
// certificates; every video plays right here.
//   list_header  plain spacing, hairline below  h1 and the lead
//   toolbar      plain spacing                  Cari video, and one chip per group (Semua first), with counts
//   results      card grid                      the count line, then one card per video: a 16:9 lite embed
//                                               (thumbnail and play button, swapped for the nocookie iframe on
//                                               Putar), title, source and duration, summary, group labels.
//                                               1 column, 2 from 640px, 3 from 1024px
// Design review ui.region_gate: results card (1.00), toolbar plain-spacing (0.59).
// ?q= and ?kelompok=<courseId> hold the search and the group, so reload and back keep them.
// Only one video plays: starting another swaps the previous one back to its thumbnail.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as API from "../api";
import { formatCount, formatMinutes } from "../lib/format";
import { setQuery, useLocation } from "../router";
import { Badge, EmptyState, Notice, PageHeader, SearchInput, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const LEARNING_LEAD = "Video panduan singkat seputar pengadaan barang/jasa. Cari topik yang membingungkan, lalu tonton langsung di sini.";

export function embedUrl(youtubeId: string): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?rel=0&autoplay=1`;
}

export function thumbUrl(youtubeId: string): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(youtubeId)}/hqdefault.jpg`;
}

/** One card: a lesson with every group it appears in (several only in Semua). */
export interface VideoItem {
  key: string;
  lesson: API.Lesson;
  groups: { id: string; title: string }[];
}

/** Lowercase without accents, so "Pengadaan" matches "pengadaan" and "é" matches "e". */
export function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

export function searchTerms(q: string): string[] {
  return fold(q).split(/\s+/).filter(Boolean);
}

/** Every term must appear in the title, summary, source, or a group title. */
export function matches(item: VideoItem, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const hay = fold([item.lesson.title, item.lesson.summary, item.lesson.source, ...item.groups.map((g) => g.title)].join(" "));
  return terms.every((t) => hay.includes(t));
}

/** Semua: each youtubeId once, in file order, with all its groups. */
export function allVideos(courses: API.LearningCourse[]): VideoItem[] {
  const byId = new Map<string, VideoItem>();
  for (const c of courses) {
    for (const l of c.lessons) {
      const seen = byId.get(l.youtubeId);
      if (seen) {
        if (!seen.groups.some((g) => g.id === c.id)) seen.groups.push({ id: c.id, title: c.title });
      } else byId.set(l.youtubeId, { key: `v:${l.youtubeId}`, lesson: l, groups: [{ id: c.id, title: c.title }] });
    }
  }
  return [...byId.values()];
}

/** One group: every lesson in file order. */
export function groupVideos(course: API.LearningCourse): VideoItem[] {
  return course.lessons.map((l) => ({ key: `${course.id}:${l.id}`, lesson: l, groups: [{ id: course.id, title: course.title }] }));
}

export function LearningScreen() {
  useTitle("E-Learning");
  const courses = useAsync(() => API.listCourses(), []);
  const hasVideos = (courses.data ?? []).some((c) => c.lessons.length > 0);

  return (
    <>
      <PageHeader divided title="E-Learning" lead={LEARNING_LEAD} />
      {courses.loading && !courses.data ? (
        <SkeletonRows count={3} label="Memuat video" />
      ) : courses.error ? (
        <Notice
          tone="danger"
          title="Video belum dapat dimuat"
          action={
            <button type="button" className="btn btn-secondary" onClick={courses.reload}>
              Muat ulang
            </button>
          }
        >
          <p>{API.errorMessage(courses.error)}</p>
        </Notice>
      ) : courses.data && !hasVideos ? (
        <EmptyState icon="book" title="Belum ada video" body="Video panduan untuk peran Anda akan tampil di sini setelah administrator menambahkannya." />
      ) : courses.data ? (
        <Library courses={courses.data.filter((c) => c.lessons.length > 0)} />
      ) : null}
    </>
  );
}

function Library({ courses }: { courses: API.LearningCourse[] }) {
  const ids = useId();
  const loc = useLocation();
  const q = loc.query.get("q") ?? "";
  const requested = loc.query.get("kelompok");
  const group = courses.find((c) => c.id === requested) ?? null;

  // The field filters as you type; the URL follows shortly after.
  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);
  useEffect(() => {
    if (search.trim() === q) return;
    const t = setTimeout(() => setQuery({ q: search.trim() || undefined }), 300);
    return () => clearTimeout(t);
  }, [search, q]);

  const terms = useMemo(() => searchTerms(search), [search]);
  const all = useMemo(() => allVideos(courses), [courses]);
  const perGroup = useMemo(() => new Map(courses.map((c) => [c.id, groupVideos(c)])), [courses]);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const [id, items] of perGroup) m.set(id, items.filter((i) => matches(i, terms)).length);
    return m;
  }, [perGroup, terms]);
  const visible = useMemo(() => (group ? perGroup.get(group.id) ?? [] : all).filter((i) => matches(i, terms)), [group, perGroup, all, terms]);
  const allCount = useMemo(() => all.filter((i) => matches(i, terms)).length, [all, terms]);

  const [playing, setPlaying] = useState<string | null>(null);
  useEffect(() => {
    if (playing && !visible.some((i) => i.key === playing)) setPlaying(null);
  }, [playing, visible]);

  const filtered = terms.length > 0 || group !== null;
  const clear = () => {
    setSearch("");
    setQuery({ q: undefined, kelompok: undefined });
  };

  return (
    <>
      <section className="toolbar learn-toolbar" aria-label="Cari dan pilih kelompok video">
        <div className="field learn-search">
          <label htmlFor={`${ids}q`}>Cari video</label>
          <SearchInput id={`${ids}q`} value={search} onChange={setSearch} placeholder="Cari video, misalnya HPS, RUP, SPSE" />
        </div>
        <div className="learn-chips" role="group" aria-label="Kelompok video">
          <GroupChip label="Semua" count={allCount} pressed={group === null} onClick={() => setQuery({ kelompok: undefined })} />
          {courses.map((c) => (
            <GroupChip key={c.id} label={c.title} count={counts.get(c.id) ?? 0} pressed={group?.id === c.id} onClick={() => setQuery({ kelompok: c.id })} />
          ))}
        </div>
      </section>

      <section className="region learn-results" aria-labelledby={`${ids}count`}>
        <p id={`${ids}count`} className="learn-count" role="status" aria-live="polite">
          <span className="num">{formatCount(visible.length)}</span> video
        </p>
        {visible.length === 0 ? (
          <EmptyState
            icon="searchNormal"
            title="Tidak ada video yang cocok"
            body={filtered ? "Coba kata kunci lain atau tampilkan semua kelompok." : "Belum ada video di kelompok ini."}
            action={
              filtered ? (
                <button type="button" className="btn btn-secondary" onClick={clear}>
                  Hapus filter
                </button>
              ) : undefined
            }
          />
        ) : (
          <ul className="video-grid">
            {visible.map((item) => (
              <VideoCard key={item.key} item={item} playing={playing === item.key} onPlay={() => setPlaying(item.key)} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function GroupChip({ label, count, pressed, onClick }: { label: string; count: number; pressed: boolean; onClick: () => void }) {
  return (
    <button type="button" className="learn-chip" aria-pressed={pressed} onClick={onClick}>
      <span className="learn-chip-label">{label}</span>
      <span className="learn-chip-count num" aria-label={`${formatCount(count)} video`}>
        {formatCount(count)}
      </span>
    </button>
  );
}

function VideoCard({ item, playing, onPlay }: { item: VideoItem; playing: boolean; onPlay: () => void }) {
  const ids = useId();
  const { lesson, groups } = item;
  const frame = useRef<HTMLIFrameElement>(null);
  const duration = formatMinutes(lesson.durationMinutes);
  const facts = [lesson.source ? `Sumber: ${lesson.source}` : null, duration].filter(Boolean).join(", ");

  // The play button leaves with the click; focus moves to the player that replaces it.
  useEffect(() => {
    if (playing) frame.current?.focus();
  }, [playing]);

  return (
    <li className="video-card" aria-labelledby={`${ids}title`}>
      <div className="video-frame">
        {playing ? (
          <iframe
            ref={frame}
            src={embedUrl(lesson.youtubeId)}
            title={lesson.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <button type="button" className="video-play" aria-label={`Putar ${lesson.title}`} onClick={onPlay}>
            <img src={thumbUrl(lesson.youtubeId)} alt="" width={480} height={360} loading="lazy" decoding="async" />
            <span className="video-play-disc" aria-hidden="true">
              <Glyph name="play" size={24} />
            </span>
          </button>
        )}
      </div>
      <div className="video-body">
        <h2 id={`${ids}title`} className="video-title">
          {lesson.title}
        </h2>
        {facts ? <p className="video-facts">{facts}</p> : null}
        {lesson.summary ? <Summary text={lesson.summary} /> : null}
        <p className="video-groups">
          <span className="visually-hidden">Kelompok: </span>
          {groups.map((g) => (
            <Badge key={g.id}>{g.title}</Badge>
          ))}
        </p>
      </div>
    </li>
  );
}

/** Three lines, with Selengkapnya when the text runs longer; the full text stays in the DOM. */
function Summary({ text }: { text: string }) {
  const id = useId();
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const measure = () => setLong(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, open]);

  return (
    <div className="video-summary-wrap">
      <p id={id} ref={ref} className="video-summary" data-open={open ? "" : undefined}>
        {text}
      </p>
      {long || open ? (
        <button type="button" className="btn btn-ghost video-more" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
          {open ? "Ringkas" : "Selengkapnya"}
        </button>
      ) : null}
    </div>
  );
}
